import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  MdAdd,
  MdArticle,
  MdGridView,
  MdViewList,
  MdNewspaper,
} from "react-icons/md";

// Composants réutilisables
import { ActualityService } from "service/actuality.service";
import { usePageManager } from "hooks/usePageManager";
import { usePhotoUrl } from "hooks/usePhotoUrl";

// Composants spécifiques
import ActualityCard from "./components/actuality.card";
import ActualityDetails from "./components/actuality.details";
import FormField from "components/common/formfield";
import PageLayout from "layouts/pagelayout";
import PageHeader from "components/common/pageheader";
import SearchFilters from "components/common/searchfilters";
import EmptyState from "components/common/emptystate";
import Pagination from "components/common/paginnation";
import FormModal from "components/common/formmodal";

// Utilitaires
const getActualityStatus = (actuality) => {
  const now = new Date();
  const publishDate = new Date(actuality.date);
  const daysDiff = Math.floor((now - publishDate) / (1000 * 60 * 60 * 24));

  if (daysDiff === 0) {
    return "Aujourd'hui";
  } else if (daysDiff === 1) {
    return "Hier";
  } else if (daysDiff <= 7) {
    return "Cette semaine";
  } else if (daysDiff <= 30) {
    return "Ce mois";
  } else {
    return "Ancien";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "Aujourd'hui":
      return "bg-green-500";
    case "Hier":
      return "bg-blue-500";
    case "Cette semaine":
      return "bg-yellow-500";
    case "Ce mois":
      return "bg-orange-500";
    case "Ancien":
      return "bg-gray-500";
    default:
      return "bg-gray-500";
  }
};

const filterOptions = [
  { value: "all", label: "Tous", activeClass: "bg-[#2ca01c]" },
  { value: "news", label: "Actualités", activeClass: "bg-blue-500" },
  { value: "announcement", label: "Annonces", activeClass: "bg-yellow-500" },
  { value: "event", label: "Événements", activeClass: "bg-purple-500" },
  { value: "press", label: "Presse", activeClass: "bg-red-500" },
];

const typeOptions = [
  { value: "", label: "Sélectionnez un type" },
  { value: "news", label: "Actualités" },
  { value: "announcement", label: "Annonces" },
  { value: "event", label: "Événements" },
  { value: "press", label: "Presse" },
];

const categoryOptions = [
  { value: "", label: "Sélectionnez une catégorie" },
  { value: "Infrastructure", label: "Infrastructure" },
  { value: "Personnel", label: "Personnel" },
  { value: "Équipement", label: "Équipement" },
  { value: "Recherche", label: "Recherche" },
  { value: "Formation", label: "Formation" },
  { value: "Partenariat", label: "Partenariat" },
  { value: "Service", label: "Service" },
];

const ActualitiesPage = () => {
  // États de base
  const [actualities, setActualities] = useState([]);
  const [filteredActualities, setFilteredActualities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedActuality, setSelectedActuality] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailView, setDetailView] = useState(false);
  const [actualityToView, setActualityToView] = useState(null);

  // État du formulaire
  const [formData, setFormData] = useState({
    author: "HiQaody", // Utilisateur connecté actuel
    date: "2025-07-02", // Date actuelle
    title: "",
    tags: "",
    content: "",
    type: "",
    description: "",
    category: "",
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);

  // Hook de gestion de page
  const {
    currentPage,
    totalPages,
    searchTerm,
    filterStatus,
    showFilters,
    viewMode,
    currentItems: currentActualities,
    setSearchTerm,
    setFilterStatus,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
    resetFilters,
    toggleViewMode,
    toggleFilters,
  } = usePageManager(filteredActualities, 6);

  // Hook pour les URLs des images
  const { getPhotoUrl } = usePhotoUrl();

  // Effects
  useEffect(() => {
    loadActualities();
  }, []);

  useEffect(() => {
    filterActualities();
  }, [actualities, searchTerm, filterStatus]);

  // Fonctions de chargement des données
  const loadActualities = async () => {
    setIsLoading(true);
    try {
      const data = await ActualityService.getAllActualities();
      setActualities(data);
      setFilteredActualities(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des actualités:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de récupérer les actualités. Veuillez réessayer plus tard.",
        confirmButtonColor: "#2ca01c",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction de filtrage
  const filterActualities = () => {
    let results = [...actualities];

    if (filterStatus !== "all") {
      results = results.filter((actuality) => actuality.type === filterStatus);
    }

    if (searchTerm.trim() !== "") {
      const searchTermLower = searchTerm.toLowerCase().trim();
      results = results.filter(
        (actuality) => {
          // Convertir tags array en string pour la recherche
          const tagsString = Array.isArray(actuality.tags) 
            ? actuality.tags.join(' ') 
            : actuality.tags || '';
            
          return (
            actuality.title?.toLowerCase().includes(searchTermLower) ||
            actuality.description?.toLowerCase().includes(searchTermLower) ||
            actuality.content?.toLowerCase().includes(searchTermLower) ||
            actuality.author?.toLowerCase().includes(searchTermLower) ||
            actuality.category?.toLowerCase().includes(searchTermLower) ||
            tagsString.toLowerCase().includes(searchTermLower)
          );
        }
      );
    }

    setFilteredActualities(results);
  };

  // Gestionnaires de formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, image: file }));
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleImageUploadClick = () => {
    document.getElementById("image").click();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      document.getElementById("image").click();
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, image: null }));
    document.getElementById("image").value = "";
  };

  const resetForm = () => {
    setFormData({
      author: "HiQaody",
      date: "2025-07-02",
      title: "",
      tags: "",
      content: "",
      type: "",
      description: "",
      category: "",
      image: null,
    });
    setImagePreview(null);
    setSelectedActuality(null);
  };

  const openForm = (actuality = null) => {
    if (actuality) {
      // Convertir tags array en string pour l'édition
      const tagsString = Array.isArray(actuality.tags) 
        ? actuality.tags.join(', ') 
        : actuality.tags || '';

      setFormData({
        author: actuality.author,
        date: actuality.date,
        title: actuality.title,
        tags: tagsString,
        content: actuality.content,
        type: actuality.type,
        description: actuality.description,
        category: actuality.category,
        image: null,
      });
      setSelectedActuality(actuality);
      
      // Définir l'aperçu de l'image si elle existe
      if (actuality.image) {
        setImagePreview(actuality.image);
      }
    } else {
      resetForm();
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.title || !formData.content || !formData.type || !formData.category) {
      Swal.fire({
        icon: "warning",
        title: "Champs requis",
        text: "Veuillez remplir tous les champs obligatoires.",
        confirmButtonColor: "#2ca01c",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      if (selectedActuality) {
        await ActualityService.updateActuality(selectedActuality.id, formData);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "L'actualité a été mise à jour avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      } else {
        await ActualityService.createActuality(formData);
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: "L'actualité a été créée avec succès!",
          confirmButtonColor: "#2ca01c",
        });
      }

      loadActualities();
      closeForm();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de l'actualité:", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Une erreur est survenue lors de l'enregistrement de l'actualité.",
        confirmButtonColor: "#2ca01c",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Êtes-vous sûr?",
      text: "Cette action est irréversible!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2ca01c",
      cancelButtonColor: "#e21b1b",
      confirmButtonText: "Oui, supprimer!",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await ActualityService.deleteActuality(id);
          setActualities(actualities.filter((actuality) => actuality.id !== id));
          setFilteredActualities(
            filteredActualities.filter((actuality) => actuality.id !== id)
          );
          Swal.fire({
            title: "Supprimé!",
            text: "L'actualité a été supprimée avec succès.",
            icon: "success",
            confirmButtonColor: "#2ca01c",
          });
        } catch (error) {
          console.error("Erreur lors de la suppression de l'actualité:", error);
          Swal.fire({
            icon: "error",
            title: "Erreur",
            text: "Une erreur est survenue lors de la suppression de l'actualité.",
            confirmButtonColor: "#2ca01c",
          });
        }
      }
    });
  };

  // Gestionnaires de vue détaillée
  const viewActualityDetails = (actuality) => {
    setActualityToView(actuality);
    setDetailView(true);
  };

  const closeActualityDetails = () => {
    setActualityToView(null);
    setDetailView(false);
  };

  // Actions du header
  const headerActions = (
    <button
      className="group flex items-center rounded-full bg-[#2ca01c] px-6 py-3 text-white shadow-sm transition-all duration-300 hover:bg-[#238615] hover:shadow-lg"
      onClick={() => openForm()}
    >
      <MdAdd className="mr-2 rounded-full bg-white text-xl text-[#2ca01c] group-hover:bg-[#f0f0f0]" />
      Nouvelle actualité
    </button>
  );

  // Contenu du formulaire
  const formFields = (
    <>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField
          label="Auteur"
          name="author"
          value={formData.author}
          onChange={handleInputChange}
          placeholder="Nom de l'auteur"
          required
        />

        <FormField
          type="date"
          label="Date de publication"
          name="date"
          value={formData.date}
          onChange={handleInputChange}
          required
        />
      </div>

      <FormField
        label="Titre"
        name="title"
        value={formData.title}
        onChange={handleInputChange}
        placeholder="Titre de l'actualité"
        required
      />

      <FormField
        type="textarea"
        label="Description courte"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        placeholder="Brève description de l'actualité"
        rows={3}
        required
      />

      <FormField
        type="textarea"
        label="Contenu complet"
        name="content"
        value={formData.content}
        onChange={handleInputChange}
        placeholder="Contenu détaillé de l'actualité"
        rows={8}
        required
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField
          type="select"
          label="Type"
          name="type"
          value={formData.type}
          onChange={handleInputChange}
          options={typeOptions}
          required
        />

        <FormField
          type="select"
          label="Catégorie"
          name="category"
          value={formData.category}
          onChange={handleInputChange}
          options={categoryOptions}
          required
        />
      </div>

      <FormField
        label="Tags"
        name="tags"
        value={formData.tags}
        onChange={handleInputChange}
        placeholder="Séparez les tags par des virgules (ex: soins intensifs, équipement médical, innovation)"
      />

      <FormField
        type="file"
        label="Image"
        name="image"
        onPhotoChange={handleImageChange}
        onPhotoUpload={handleImageUploadClick}
        onKeyDown={handleKeyDown}
        photoPreview={imagePreview}
        onRemovePhoto={handleRemoveImage}
      />
    </>
  );

  // Rendu principal
  return (
    <PageLayout>
      {detailView && actualityToView ? (
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <ActualityDetails
            actuality={actualityToView}
            onClose={closeActualityDetails}
            getPhotoUrl={getPhotoUrl}
          />
        </div>
      ) : (
        <>
          <PageHeader
            title="Actualités"
            subtitle="Centre Hospitalier Universitaire Tambohobe"
            icon={MdNewspaper}
            actions={headerActions}
            viewMode={viewMode}
            onViewModeChange={toggleViewMode}
            ViewModeIcon={viewMode === "grid" ? MdViewList : MdGridView}
            viewModeText={viewMode === "grid" ? "Vue liste" : "Vue grille"}
          />

          <SearchFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={toggleFilters}
            filterStatus={filterStatus}
            onFilterStatusChange={setFilterStatus}
            onResetFilters={resetFilters}
            filteredItemsCount={filteredActualities.length}
            currentPage={currentPage}
            itemsPerPage={6}
            filterOptions={filterOptions}
            placeholder="Rechercher une actualité..."
            itemType="actualité"
          />

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="border-b-transparent h-16 w-16 animate-spin rounded-full border-4 border-[#2ca01c]"></div>
            </div>
          ) : filteredActualities.length === 0 ? (
            <EmptyState
              icon={MdArticle}
              title={
                actualities.length === 0
                  ? "Aucune actualité disponible"
                  : "Aucune actualité trouvée"
              }
              description={
                actualities.length === 0
                  ? 'Ajoutez votre première actualité en cliquant sur le bouton "Nouvelle actualité"'
                  : "Aucune actualité ne correspond à vos critères de recherche"
              }
              actionText={
                actualities.length === 0 ? "Nouvelle actualité" : undefined
              }
              onAction={actualities.length === 0 ? () => openForm() : undefined}
              showResetFilters={true}
              onResetFilters={resetFilters}
              hasItems={actualities.length > 0}
              hasActiveFilters={searchTerm || filterStatus !== "all"}
            />
          ) : (
            <>
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
                    : "space-y-6"
                }
              >
                {currentActualities.map((actuality) => (
                  <ActualityCard
                    key={actuality.id}
                    actuality={actuality}
                    onEdit={openForm}
                    onDelete={handleDelete}
                    onView={viewActualityDetails}
                    viewMode={viewMode}
                    getPhotoUrl={getPhotoUrl}
                    getActualityStatus={getActualityStatus}
                    getStatusColor={getStatusColor}
                  />
                ))}
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onFirstPage={goToFirstPage}
                onPreviousPage={goToPreviousPage}
                onNextPage={goToNextPage}
                onLastPage={goToLastPage}
              />
            </>
          )}
        </>
      )}

      <FormModal
        isOpen={showForm}
        onClose={closeForm}
        title={selectedActuality ? "Modifier l'actualité" : "Nouvelle actualité"}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isUpdate={!!selectedActuality}
      >
        {formFields}
      </FormModal>
    </PageLayout>
  );
};

export default ActualitiesPage;